import React, { useState, useEffect, useRef } from "react";
import { emailAPI, authAPI, apiUtils } from "../services/apiService.js";
import config from "../config.js";

import {
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaCog,
  FaUserShield,
  FaBan,
  FaShieldAlt,
  FaUser,
  FaBuilding,
  FaPaperPlane,
  FaFolderOpen,
  FaTrash,
} from "react-icons/fa";

export default function App() {
  const [activeUserEmail, setActiveUserEmail] = useState(null);
  const [currentMessage, setCurrentMessage] = useState(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Permission and user state
  const [hasPermission, setHasPermission] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  // Admin configuration state
  const [adminSettings, setAdminSettings] = useState({
    allowMove: false,
    allowDelete: false,
    forwardEmail: "",
    emailSubject: "",
    emailBody: "",
    allowUserFeedback: false,
    endpointUrl: "",
  });

  // User feedback state
  const textareaRef = useRef(null);

  // Form states
  const [forwardTo, setForwardTo] = useState("");

  useEffect(() => {
    initializeApp();
  }, []);

  // Load admin settings when userInfo changes
  useEffect(() => {
    if (userInfo && isAdmin) {
      loadAdminSettings();
    }
  }, [userInfo, isAdmin]);

  // Handle user feedback checkbox change
  useEffect(() => {
    if (adminSettings.allowUserFeedback) {
      // When user feedback is enabled, add @data prefix to textarea
      if (textareaRef.current) {
        const currentValue = textareaRef.current.value;
        if (!currentValue.startsWith("@data")) {
          textareaRef.current.value = `@data ${currentValue}`;
        }
      }
    } else {
      // When user feedback is disabled, remove @data prefix if present
      if (textareaRef.current) {
        const currentValue = textareaRef.current.value;
        if (currentValue.startsWith("@data")) {
          textareaRef.current.value = currentValue.substring(6);
        }
      }
    }
  }, [adminSettings.allowUserFeedback]);

  const initializeApp = async () => {
    try {
      // Get user email from Office context
      const userProfile = Office.context.mailbox.userProfile;
      // console.log("User profile:", userProfile);
      // console.log(Office.context.mailbox.item);
      // console.log(Office.context.mailbox.item.to[0].emailAddress);

      // setActiveUserEmail(userProfile.emailAddress);
      // setActiveUserEmail(Office.context.mailbox.item.to[0].emailAddress);
      setActiveUserEmail(userProfile.emailAddress);

      // Check user permission first
      await checkUserPermission(userProfile.emailAddress);
      // await checkUserPermission(Office.context.mailbox.item.to[0].emailAddress);

      // Get current message details
      await getCurrentMessage();
    } catch (error) {
      console.error("Initialization error:", error);
      showMessage(config.UI.messages.initializationFailed, "error");
    }
  };

  const checkUserPermission = async (email) => {
    try {
      setLoading(true);
      console.log("Checking permission for email:", email);

      const response = await authAPI.checkUserPermission(email.toLowerCase());

      console.log("Permission check response:", response);

      if (response.success && response.hasPermission) {
        setHasPermission(true);
        setPermissionError(null); // Clear any previous errors
        setUserInfo(response.user);
        setOrganization(response.organization);
        setIsAdmin(response.isAdmin);

        // For admins, always show admin configuration screen
        if (response.isAdmin) {
          // Set userInfo, settings will be loaded via useEffect
          if (response.user) {
            setUserInfo(response.user);
          }
        }
      } else {
        // Handle different types of permission errors
        setHasPermission(false);

        if (response.error && response.error.includes("No administrator has configured")) {
          // User exists but no admin configured - show different message
          setPermissionError("no_admin_configured");
          showMessage(
            config.UI.messages.noAdminConfigured,
            "warning"
          );
        } else if (response.error && response.error.includes("User not found")) {
          // User doesn't exist in database
          setPermissionError("user_not_found");
          showMessage(
            config.UI.messages.userNotFound,
            "error"
          );
        } else {
          // Other errors
          setPermissionError("other");
          showMessage(
            response.error || config.UI.messages.accessDenied,
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Permission check error:", error);
      setHasPermission(false);
      showMessage(apiUtils.formatErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentMessage = async () => {
    try {
      const item = Office.context.mailbox.item;
      if (item.itemType === Office.MailboxEnums.ItemType.Message) {
        const messageId = item.itemId;
        const subject = item.subject;
        const sender = item.from?.emailAddress;

        setCurrentMessage({
          id: messageId,
          subject: subject,
          sender: sender,
          receivedTime: item.dateTimeCreated,
        });
      }
    } catch (error) {
      console.error("Error getting current message:", error);
    }
  };

  const loadAdminSettings = async () => {
    try {
      // Settings are now loaded from userInfo in checkUserPermission
      if (userInfo) {
        // Handle email body - if it contains @data prefix, extract the text after it
        let emailBody = userInfo.emailBody || "";
        if (userInfo.allowUserFeedback && emailBody.startsWith("@data")) {
          emailBody = emailBody.substring(6); // Remove @data prefix for internal storage
        }

        setAdminSettings({
          allowMove: userInfo.allowedActions?.move || false,
          allowDelete: userInfo.allowedActions?.delete || false,
          forwardEmail: userInfo.forwardEmail || "",
          emailSubject: userInfo.emailSubject || "",
          emailBody: emailBody,
          allowUserFeedback: userInfo.allowUserFeedback || false,
          endpointUrl: userInfo.endpointUrl || "",
        });
      }
    } catch (error) {
      console.error("Error loading admin settings:", error);
    }
  };

  const handleSaveAdminSettings = async () => {
    try {
      setLoading(true);

      // Validate mandatory forward email
      if (!adminSettings.forwardEmail.trim()) {
        showMessage(config.UI.messages.forwardEmailRequired, "error");
        setLoading(false);
        return;
      }

      const syncData = {
        allowedActions: {
          move: adminSettings.allowMove,
          delete: adminSettings.allowDelete,
        },
        forwardEmail: adminSettings.forwardEmail,
        emailSubject: adminSettings.emailSubject,
        allowUserFeedback: adminSettings.allowUserFeedback,
        endpointUrl: adminSettings.endpointUrl,
      };

      // Only include emailBody if user feedback is enabled
      if (adminSettings.allowUserFeedback) {
        // Include @data prefix in the email body when saving
        syncData.emailBody = `@data ${adminSettings.emailBody}`;
      }

      const response = await authAPI.syncUserSettings(activeUserEmail, syncData);

      if (response.success) {
        showMessage(config.UI.messages.settingsSaveSuccess, "success");

        // Refresh user info and settings
        await checkUserPermission(activeUserEmail);
      }
    } catch (error) {
      console.error("Save settings error:", error);
      showMessage(apiUtils.formatErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = "info") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <FaCheckCircle className="w-4 h-4 text-green-500" />;
      case "failed":
        return <FaTimesCircle className="w-4 h-4 text-red-500" />;
      case "pending":
        return <div className="w-4 h-4 text-blue-500 animate-spin">⏳</div>;
      default:
        return <FaInfoCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getMessageBarClass = (type) => {
    switch (type) {
      case "success":
        return "bg-green-50 border border-green-200 text-green-800";
      case "error":
        return "bg-red-50 border border-red-200 text-red-800";
      case "warning":
        return "bg-yellow-50 border border-yellow-200 text-yellow-800";
      default:
        return "bg-blue-50 border border-blue-200 text-blue-800";
    }
  };

  // Show different screens based on permission error type
  if (!hasPermission) {
    return (
      <div className="p-4 max-w-md min-h-[500px] flex flex-col items-center justify-center text-center">
        <FaBan className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600 mb-4">
          {config.UI.messages.accessDenied}
        </p>
        <div className="text-sm text-gray-500">
          <p>User: {activeUserEmail}</p>
          {permissionError === "user_not_found" && <p>Status: User not found in database</p>}
        </div>
      </div>
    );
  }

  // Show admin configuration screen for all admins
  if (isAdmin) {
    return (
      <div className="p-4 max-w-md min-h-[500px] flex flex-col gap-4">
        {/* <div className="flex items-center gap-2 mb-4">
          <FaCog className="w-6 h-6 text-[#0C75D1]" />
          <h2 className="text-xl font-semibold text-gray-900">Admin Configuration</h2>
        </div> */}

        {message && (
          <div className={`p-3 rounded-md ${getMessageBarClass(message.type)}`}>{message.text}</div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Organization Settings</h3>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Forward Email Address:
              </label>
              <input
                type="email"
                value={adminSettings.forwardEmail}
                onChange={(e) =>
                  setAdminSettings({ ...adminSettings, forwardEmail: e.target.value })
                }
                placeholder={config.UI.placeholders.forwardEmail}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Endpoint URL:</label>
              <input
                type="url"
                value={adminSettings.endpointUrl}
                readOnly
                placeholder={config.UI.placeholders.endpointUrl}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Add Subject:</label>
              <input
                type="text"
                value={adminSettings.emailSubject}
                onChange={(e) =>
                  setAdminSettings({ ...adminSettings, emailSubject: e.target.value })
                }
                placeholder={config.UI.placeholders.emailSubject}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add email Body:
              </label>
              {/* <input
                type="email"
                value={adminSettings.forwardEmail}
                onChange={(e) =>
                  setAdminSettings({ ...adminSettings, forwardEmail: e.target.value })
                }
                placeholder="security@company.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              /> */}
              <textarea
                ref={textareaRef}
                 placeholder={config.UI.placeholders.emailBody}
                name="emailBody"
                id="emailBody"
                defaultValue={
                  adminSettings.allowUserFeedback
                    ? `@data ${adminSettings.emailBody}`
                    : adminSettings.emailBody
                }
                onInput={(e) => {
                  const fullText = e.target.value;

                  if (adminSettings.allowUserFeedback) {
                    // If @data prefix is present, extract text after it
                    if (fullText.startsWith("@data")) {
                      const textAfterPrefix = fullText.substring(6);
                      setAdminSettings({ ...adminSettings, emailBody: textAfterPrefix });
                    } else {
                      // If @data was removed, restore it
                      setAdminSettings({ ...adminSettings, emailBody: fullText });
                      // Restore the @data prefix
                      setTimeout(() => {
                        if (textareaRef.current) {
                          textareaRef.current.value = `@data ${fullText}`;
                        }
                      }, 0);
                    }
                  } else {
                    setAdminSettings({ ...adminSettings, emailBody: fullText });
                  }
                }}
                onKeyDown={(e) => {
                  if (adminSettings.allowUserFeedback) {
                    // Prevent backspace/delete from removing @data prefix
                    if (e.key === "Backspace" && e.target.selectionStart <= 6) {
                      e.preventDefault();
                    }
                    // Prevent delete key from removing @data prefix
                    if (e.key === "Delete" && e.target.selectionStart < 6) {
                      e.preventDefault();
                    }
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Enable Actions:</h4>

              {/* <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={adminSettings.allowForward}
                  onChange={(e) =>
                    setAdminSettings({ ...adminSettings, allowForward: e.target.checked })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Allow Forward Email {adminSettings.targetFolder}
                </span>
              </label> */}

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={adminSettings.allowMove}
                  onChange={(e) =>
                    setAdminSettings({ ...adminSettings, allowMove: e.target.checked })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Allow Move Email to Junk</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={adminSettings.allowDelete}
                  onChange={(e) =>
                    setAdminSettings({ ...adminSettings, allowDelete: e.target.checked })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Allow Delete Email</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={adminSettings.allowUserFeedback}
                  onChange={(e) =>
                    setAdminSettings({ ...adminSettings, allowUserFeedback: e.target.checked })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Allow user feedback</span>
              </label>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSaveAdminSettings}
                disabled={loading || !adminSettings.forwardEmail.trim()}
                className="flex-1 bg-[#46A2F2] text-white px-4 py-2 rounded-md hover:bg-[#0C75D1] focus:outline-none focus:ring-2 focus:ring-[#337ab7] focus:ring-offset-2 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed text-sm"
              >
                {loading ? "Saving..." : "Save Settings"}
              </button>
            </div>
            {!adminSettings.forwardEmail.trim() && (
              <div className="text-sm text-red-600 mt-2">
                {config.UI.messages.forwardEmailRequiredMessage}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 mt-auto">
          <div className="text-xs text-gray-500">
            <div>Admin: {activeUserEmail}</div>
          </div>
        </div>
      </div>
    );
  }
}


// import React, { useEffect, useState } from 'react'
// import { createNestablePublicClientApplication } from "@azure/msal-browser";

// let pca;

// export default function App() {
//   const [isInitialized, setIsInitialized] = useState(false);
//   const [userInfo, setUserInfo] = useState(null);
//   const [messages, setMessages] = useState([]);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const initializeMSAL = async () => {
//       try {
//         // Wait for Office to be ready
//         await new Promise((resolve) => {
//           if (window.Office && window.Office.onReady) {
//             window.Office.onReady(resolve);
//           } else {
//             resolve();
//           }
//         });

//         // Init MSAL with NAA support
//         pca = await createNestablePublicClientApplication({
//           auth: {
//             clientId: "50eda291-dc48-47f8-8b53-a3e68df0df2b",
//             authority: "https://login.microsoftonline.com/common",
//           },
//         });

//         setIsInitialized(true);
//       } catch (err) {
//         console.error("Failed to initialize MSAL:", err);
//         setError("Failed to initialize authentication");
//       }
//     };

//     initializeMSAL();
//   }, []);

//   // Strict "no-UI" token acquisition helper:
//   const acquireGraphTokenSilent = async (scopes) => {
//     const request = { scopes };
//     const result = await pca.acquireTokenSilent(request);
//     return result.accessToken;
//   };

//   // Example call to Microsoft Graph (/me and top 5 messages)
//   const run = async () => {
//     try {
//       setError(null);

//       const token = await acquireGraphTokenSilent(["User.Read", "openid", "profile"]);
//       const meRes = await fetch("https://graph.microsoft.com/v1.0/me", {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       if (!meRes.ok) throw new Error(await meRes.text());
//       const me = await meRes.json();
//       console.log("Hello,", me.displayName);
//       console.log(me);
//       setUserInfo(me);

//       const mailToken = await pca.acquireTokenSilent({ scopes: ["Mail.Read", "openid", "profile"] });
//       const msgRes = await fetch("https://graph.microsoft.com/v1.0/me/messages?$top=5", {
//         headers: { Authorization: `Bearer ${mailToken.accessToken}` },
//       });
//       if (!msgRes.ok) throw new Error(await msgRes.text());
//       const { value: messages } = await msgRes.json();
//       console.log("Sample subjects:", messages.map(m => m.subject));
//       setMessages(messages);
//     } catch (err) {
//       console.error("Silent token failed:", err);
//       setError(`Authentication failed: ${err.message}`);
//     }
//   };

//   if (!isInitialized) {
//     return (
//       <div style={{ padding: '20px' }}>
//         <h2>Initializing...</h2>
//         <p>Setting up authentication...</p>
//       </div>
//     );
//   }

//   return (
//     <div style={{ padding: '20px' }}>
//       <h2>Spam Report Add-in</h2>

//       <button
//         id="runButton"
//         onClick={run}
//         style={{
//           padding: '10px 20px',
//           backgroundColor: '#0078d4',
//           color: 'white',
//           border: 'none',
//           borderRadius: '4px',
//           cursor: 'pointer',
//           fontSize: '14px',
//           marginBottom: '20px'
//         }}
//       >
//         Connect to Microsoft Graph
//       </button>

//       {error && (
//         <div style={{
//           color: 'red',
//           backgroundColor: '#ffe6e6',
//           padding: '10px',
//           borderRadius: '4px',
//           marginBottom: '20px'
//         }}>
//           {error}
//         </div>
//       )}

//       {userInfo && (
//         <div style={{ marginBottom: '20px' }}>
//           <h3>User Information</h3>
//           <p><strong>Name:</strong> {userInfo.displayName}</p>
//           <p><strong>Email:</strong> {userInfo.mail || userInfo.userPrincipalName}</p>
//         </div>
//       )}

//       {messages.length > 0 && (
//         <div>
//           <h3>Recent Messages</h3>
//           <ul>
//             {messages.map((message, index) => (
//               <li key={index} style={{ marginBottom: '10px' }}>
//                 <strong>Subject:</strong> {message.subject || 'No Subject'}<br/>
//                 <strong>From:</strong> {message.from?.emailAddress?.name || 'Unknown'}<br/>
//                 <strong>Received:</strong> {new Date(message.receivedDateTime).toLocaleString()}
//               </li>
//             ))}
//           </ul>
//         </div>
//       )}
//     </div>
//   );
// }
